import { useEffect, useRef, useState } from 'react';

import {
  getSiteCmsCompetitionCardsApi,
  getSiteCmsDomainApi,
  getSiteCmsPublicationStateApi,
  listSiteCmsDocumentsApi,
  listSiteCmsImagesApi,
  prepareSiteCmsPublicationApi,
  publishSiteCmsOwnedApi,
  restoreSiteCmsOwnedApi,
  unpublishSiteCmsOwnedApi,
} from '@/lib/api/siteCmsApi';
import {
  cmsCompetitionReferences,
  cmsDocumentReferences,
  cmsImageReferences,
  cmsPublicContent,
} from '@shared/cms/content';
import type { SiteCmsDraftResponse } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Globe2, Loader2, RefreshCw, Rocket, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import SiteCmsPreview from './SiteCmsPreview';
import { type CmsPublishBlocker, cmsPublishBlockers } from './site-cms-publication-gates';
import { CmsPublicationReviewError, cmsPublicationReview } from './site-cms-publication-review';
import { type CmsIssue, cmsDraftIssues } from './site-cms-workspace-model';
import { useCmsInteractions } from './use-cms-interactions';

type Props = {
  active?: boolean;
  draft: SiteCmsDraftResponse;
  unsavedChanges: boolean;
  pendingForms?: boolean;
  libraryBusy?: boolean;
  accessSuspended?: boolean;
  editorReloading?: boolean;
  onSave?: () => Promise<boolean>;
  onNavigate?: (section: 'pages' | 'documents' | 'settings' | 'navigation') => void;
  onOpenIssue?: (issue: CmsIssue) => void;
};
export default function SiteCmsPublicationPanel({
  active = true,
  draft,
  unsavedChanges,
  pendingForms = false,
  libraryBusy = false,
  accessSuspended = false,
  editorReloading = false,
  onSave,
  onNavigate,
  onOpenIssue,
}: Props) {
  const { ownerType, ownerId } = draft;
  const { ask } = useCmsInteractions();
  const client = useQueryClient();
  const key = ['site-cms', 'publication-state', ownerType, ownerId];
  const mutationKey = ['site-cms', 'operation', ownerType, ownerId];
  const domain = useQuery({
    queryKey: ['site-cms', 'domain', ownerType, ownerId],
    queryFn: () => getSiteCmsDomainApi(ownerType, ownerId),
    retry: false,
  });
  const state = useQuery({
    queryKey: key,
    queryFn: () => getSiteCmsPublicationStateApi(ownerType, ownerId),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const images = useQuery({
    queryKey: ['site-cms', 'images', ownerType, ownerId],
    queryFn: () => listSiteCmsImagesApi(ownerType, ownerId),
    retry: false,
  });
  const docs = useQuery({
    queryKey: ['site-cms', 'documents', ownerType, ownerId],
    queryFn: () => listSiteCmsDocumentsApi(ownerType, ownerId),
    retry: false,
  });
  const competitionIds = cmsCompetitionReferences(cmsPublicContent(draft.pages, draft.headerMenu).pages);
  const competitions = useQuery({
    queryKey: ['site-cms', 'competition-cards', ownerType, ownerId, competitionIds.join(',')],
    queryFn: () => getSiteCmsCompetitionCardsApi(ownerType, ownerId, competitionIds),
    enabled: competitionIds.length > 0,
    retry: false,
  });
  const [reviewed, setReviewed] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [page, setPage] = useState('home');
  const [mobile, setMobile] = useState(false);
  const [failure, setFailure] = useState('');
  const latest = useRef({ draft, unsavedChanges, pendingForms, libraryBusy });
  useEffect(() => {
    latest.current = { draft, unsavedChanges, pendingForms, libraryBusy };
  }, [draft, unsavedChanges, pendingForms, libraryBusy]);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: key });
    void client.invalidateQueries({ queryKey: ['site-cms', 'images', ownerType, ownerId] });
    void client.invalidateQueries({ queryKey: ['site-cms', 'documents', ownerType, ownerId] });
  };
  const publish = useMutation({
    mutationKey,
    mutationFn: async ({ submitted, reviewKey }: { submitted: SiteCmsDraftResponse; reviewKey: string }) => {
      const [capability, verifiedDomain, checkedImages, checkedDocs, checkedCompetitions] = await Promise.all([
        state.refetch(),
        domain.refetch(),
        images.refetch(),
        docs.refetch(),
        competitionIds.length ? competitions.refetch() : Promise.resolve({ data: [], isError: false }),
      ]);
      if ([capability, verifiedDomain, checkedImages, checkedDocs, checkedCompetitions].some(result => result.isError))
        throw new CmsPublicationReviewError(
          'La vérification finale a échoué. Aucune demande de publication n’a été envoyée.',
        );
      if (!capability.data?.canPublish || capability.data.requiresResave)
        throw new CmsPublicationReviewError(
          capability.data?.reason ?? 'Les conditions ont changé. Vérifiez à nouveau cette version.',
        );
      const now = latest.current;
      const refreshedReview = cmsPublicationReview(
        now.draft,
        verifiedDomain.data,
        capability.data,
        checkedImages.data,
        checkedDocs.data,
        checkedCompetitions.data,
      );
      if (now.unsavedChanges || now.pendingForms || now.libraryBusy || refreshedReview !== reviewKey) {
        throw new CmsPublicationReviewError(
          'Le contenu, le domaine ou un fichier sélectionné a changé pendant la vérification. Confirmez la nouvelle version avant de publier.',
        );
      }
      return publishSiteCmsOwnedApi(ownerType, ownerId, submitted.revision);
    },
    onSuccess: result => {
      setReviewed(null);
      setFailure('');
      refresh();
      toast.success(`La révision ${result.revision} est en ligne. Vos nouvelles saisies restent dans le brouillon.`);
    },
    onError: error => {
      setReviewed(null);
      refresh();
      setFailure(
        error instanceof CmsPublicationReviewError
          ? `${error.message} Aucune publication n’a été envoyée.`
          : `${error.message} Le résultat doit être vérifié avant de relancer.`,
      );
    },
  });
  const prepare = useMutation({
    mutationKey,
    mutationFn: (revision: number) => prepareSiteCmsPublicationApi(ownerType, ownerId, revision),
    onSuccess: () => {
      refresh();
      setFailure('');
      toast.success('Version préparée sans modifier le site public.');
    },
    onError: error => setFailure(error.message),
  });
  const restore = useMutation({
    mutationKey,
    mutationFn: (id: string) => restoreSiteCmsOwnedApi(ownerType, ownerId, id),
    onSuccess: () => {
      refresh();
      setFailure('');
      toast.success('Version restaurée. Le brouillon actuel est conservé.');
    },
    onError: error => {
      refresh();
      setFailure(error.message);
    },
  });
  const unpublish = useMutation({
    mutationKey,
    mutationFn: () => unpublishSiteCmsOwnedApi(ownerType, ownerId),
    onSuccess: () => {
      setReviewed(null);
      refresh();
      setFailure('');
      toast.success('Diffusion retirée. Le brouillon reste conservé.');
    },
    onError: error => {
      refresh();
      setFailure(error.message);
    },
  });
  const live = state.data?.live;
  const busy = publish.isPending || prepare.isPending || restore.isPending || unpublish.isPending;
  const released = cmsPublicContent(draft.pages, draft.headerMenu);
  const docRefs = cmsDocumentReferences(released.pages, released.headerMenu);
  const imageRefs = cmsImageReferences(released.pages, draft.branding);
  const review = cmsPublicationReview(draft, domain.data, state.data, images.data, docs.data, competitions.data);
  const confirmed = reviewed === review && !unsavedChanges && !pendingForms && !libraryBusy;
  const issues = cmsDraftIssues(draft);
  const missingCompetitions = competitionIds.filter(id => !competitions.data?.some(card => card.id === id));
  const missingImages = [...imageRefs].filter(id => !images.data?.some(image => image.id === id));
  const missingDocuments = [...docRefs].filter(slug => !docs.data?.some(doc => doc.slug === slug));
  const missing = missingCompetitions.length > 0 || missingImages.length > 0 || missingDocuments.length > 0;
  const revokedRevision = state.data?.latest?.status === 'REVOKED' && state.data.latest.revision === draft.revision;
  const requiresSave = unsavedChanges || state.data?.requiresResave || revokedRevision || draft.revision === 0;
  const alreadyOnline = Boolean(live && live.revision === draft.revision && !requiresSave);
  const unavailable =
    state.isError ||
    domain.isError ||
    images.isError ||
    docs.isError ||
    (competitionIds.length > 0 && competitions.isError);
  const loading =
    state.isPending ||
    domain.isPending ||
    images.isPending ||
    docs.isPending ||
    (competitionIds.length > 0 && competitions.isPending);
  const blockers = cmsPublishBlockers({
    state: state.data,
    domain: domain.data,
    loading,
    unavailable,
    requiresSave,
    revision: draft.revision,
    alreadyOnline,
    pendingForms,
    libraryBusy,
    busy,
    accessSuspended,
    editorReloading,
    issueCount: issues.length,
    missingImageCount: missingImages.length,
    missingDocumentCount: missingDocuments.length,
    missingCompetitionCount: missingCompetitions.length,
  });
  // Reviewing a saved draft and privately preparing it must remain possible even
  // while BoulesConnect is configuring HTTPS / the external publisher.
  const canReview =
    !requiresSave &&
    !unavailable &&
    !loading &&
    !missing &&
    !issues.length &&
    !busy &&
    !pendingForms &&
    !libraryBusy &&
    !accessSuspended &&
    !editorReloading;
  // Only *public activation* needs every server/operator prerequisite.
  const publishable = canReview && blockers.length === 0 && state.data?.canPublish === true;
  const readyCount = state.data?.readiness?.filter(check => check.status === 'ready').length ?? 0;
  const stepCount = state.data?.readiness?.length ?? 0;
  const operatorBlocked = blockers.some(blocker => blocker.action === 'operator');
  const refreshChecks = () => {
    void state.refetch();
    void domain.refetch();
    void images.refetch();
    void docs.refetch();
    if (competitionIds.length) void competitions.refetch();
  };
  const openBlocker = (blocker: CmsPublishBlocker) => {
    if (blocker.action === 'save') void onSave?.();
    else if (blocker.action === 'pages') onNavigate?.('pages');
    else if (blocker.action === 'documents') onNavigate?.('documents');
    else if (blocker.action === 'retry') refreshChecks();
  };
  return (
    <div className='cms-stack' aria-busy={busy}>
      {busy && (
        <div className='cms-notice' role='status'>
          <Loader2 size={18} className='animate-spin' />
          Opération en cours. Vous pouvez changer de section ; elle continue pour la version demandée.
        </div>
      )}
      {failure && (
        <div className='cms-alert' role='alert'>
          <strong>L’opération n’est pas confirmée.</strong>
          <p>{failure}</p>
          <button
            type='button'
            className='cms-button'
            onClick={() => {
              void state.refetch();
              void domain.refetch();
            }}
          >
            Vérifier le statut
          </button>
        </div>
      )}
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <div>
            <span className='cms-kicker'>Ce que voient vos visiteurs</span>
            <h3>
              {domain.isPending
                ? 'Lecture du domaine…'
                : domain.isError
                  ? 'Domaine indisponible'
                  : (domain.data?.hostname ?? 'Adresse à configurer')}
            </h3>
          </div>
          <Globe2 size={24} />
        </div>
        {unavailable ? (
          <p role='alert' className='cms-alert'>
            La lecture du statut ou des ressources a échoué. Aucun état hors ligne n’est supposé.{' '}
            <button
              type='button'
              onClick={() => {
                void state.refetch();
                void domain.refetch();
                void images.refetch();
                void docs.refetch();
                if (competitionIds.length) void competitions.refetch();
              }}
            >
              Réessayer
            </button>
          </p>
        ) : state.isPending ? (
          <p role='status'>Vérification de la version publique…</p>
        ) : (
          <div className='cms-live-state'>
            <span className={`cms-pill ${live ? 'cms-pill-green' : ''}`}>
              {live ? `En ligne · révision ${live.revision}` : 'Site hors ligne'}
            </span>
            <p>
              {live
                ? live.revision === draft.revision && !requiresSave
                  ? 'La révision enregistrée correspond à celle en ligne.'
                  : 'Votre travail en cours ne modifie pas encore cette version.'
                : 'Vos brouillons restent privés. Vous pouvez les vérifier avant toute diffusion.'}
            </p>
            {live && (
              <a className='cms-button' href={`https://${live.hostname}`} target='_blank' rel='noreferrer'>
                Voir le site public ↗
              </a>
            )}
          </div>
        )}
        <p className='cms-hint'>
          La vérification DNS et la configuration HTTPS sont réalisées par BoulesConnect avant la première mise en
          ligne. Un aperçu local ne vaut pas approbation du domaine.
        </p>
      </section>
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <div>
            <span className='cms-kicker'>Ce que vous allez diffuser</span>
            <h3>Vérifier la révision {draft.revision}</h3>
          </div>
          <Rocket size={24} />
        </div>
        <p className='cms-muted'>La publication concerne tout le site enregistré, pas seulement la page ouverte.</p>
        {pendingForms && (
          <p className='cms-notice' role='status'>
            Un formulaire est inachevé (adresse, image, document ou nouvelle page). Terminez-le ou annulez-le avant de
            vérifier cette publication.
          </p>
        )}
        {libraryBusy && (
          <p className='cms-notice' role='status'>
            Attendez la fin de l’opération de bibliothèque avant de publier.
          </p>
        )}
        {requiresSave && (
          <div className='cms-notice'>
            <p>
              {revokedRevision
                ? 'Cette révision a été retirée. Créez une nouvelle révision sans modifier votre texte pour republier.'
                : state.data?.requiresResave
                  ? 'Des médias ont changé. Actualisez le brouillon avant de vérifier sa publication.'
                  : 'Enregistrez votre travail avant de vérifier la publication.'}
            </p>
            {onSave && (
              <button
                type='button'
                className='cms-button'
                disabled={busy || pendingForms || libraryBusy}
                onClick={() => {
                  void onSave();
                }}
              >
                {revokedRevision || state.data?.requiresResave ? 'Actualiser le brouillon' : 'Enregistrer le brouillon'}
              </button>
            )}
          </div>
        )}
        {issues.length > 0 && (
          <div className='cms-alert'>
            <strong>Corrections nécessaires</strong>
            <ul>
              {issues.slice(0, 8).map(issue => (
                <li key={issue.id}>
                  {onOpenIssue ? (
                    <button type='button' className='cms-link-button' onClick={() => onOpenIssue(issue)}>
                      {issue.message} — Corriger
                    </button>
                  ) : (
                    issue.message
                  )}
                </li>
              ))}
            </ul>
            {issues.length > 8 && <p>{issues.length - 8} autre(s) correction(s) à traiter.</p>}
          </div>
        )}
        <div className='cms-review-grid'>
          <div>
            <h4>{released.pages.length} pages incluses</h4>
            <ul>
              {released.pages.map(p => (
                <li key={p.slug}>{p.title}</li>
              ))}
            </ul>
            {draft.pages.length > released.pages.length && (
              <p>{draft.pages.length - released.pages.length} page(s) privée(s), exclue(s).</p>
            )}
          </div>
          <div>
            <h4>
              {imageRefs.size} images et {docRefs.size} PDF
            </h4>
            <ul>
              {[...docRefs].map(slug => (
                <li key={slug}>{docs.data?.find(d => d.slug === slug)?.title ?? `PDF indisponible : ${slug}`}</li>
              ))}
              {[...imageRefs].map(id => (
                <li key={id}>{images.data?.find(i => i.id === id)?.title ?? `Image indisponible : ${id}`}</li>
              ))}
            </ul>
            {!docRefs.size && !imageRefs.size && <p>Aucun média requis. Un site sans photo ni PDF peut être publié.</p>}
          </div>
        </div>
        {competitionIds.length > 0 && (
          <div className='cms-notice'>
            <strong>{competitionIds.length} concours sélectionné(s)</strong>
            <ul>
              {competitionIds.map(id => (
                <li key={id}>
                  {competitions.data?.find(card => card.id === id)?.title ?? `Concours indisponible : ${id}`}
                </li>
              ))}
            </ul>
            {missingCompetitions.length > 0 && (
              <p role='alert'>Remplacez ou retirez les concours indisponibles dans Pages avant de publier.</p>
            )}
          </div>
        )}
        <button type='button' className='cms-button' onClick={() => setPreview(v => !v)}>
          {preview ? 'Fermer la vérification visuelle' : 'Vérifier la prochaine publication'}
        </button>
        {preview && active && (
          <div className='cms-review-preview'>
            <div className='cms-row'>
              <label>
                Page à vérifier
                <select
                  value={released.pages.some(p => p.slug === page) ? page : 'home'}
                  onChange={e => setPage(e.target.value)}
                >
                  {released.pages.map(p => (
                    <option key={p.slug} value={p.slug}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </label>
              <button type='button' className='cms-button' onClick={() => setMobile(v => !v)}>
                {mobile ? 'Vue ordinateur' : 'Vue mobile'}
              </button>
            </div>
            <SiteCmsPreview draft={draft} mode='publication' pageSlug={page} mobile={mobile} onNavigatePage={setPage} />
          </div>
        )}
        <section
          className={`cms-publish-readiness ${blockers.length ? 'is-blocked' : 'is-ready'}`}
          aria-labelledby='cms-publish-readiness-title'
          id='cms-publish-explanation'
        >
          <div className='cms-publish-readiness-header'>
            <span className='cms-publish-readiness-icon'>
              {blockers.length ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
            </span>
            <div>
              <h4 id='cms-publish-readiness-title'>
                {blockers.length
                  ? `Publication bloquée : ${blockers.length} point(s) à régler`
                  : 'Toutes les conditions sont réunies'}
              </h4>
              <p>
                {blockers.length
                  ? 'Voici pourquoi le bouton est désactivé et ce qu’il reste à faire.'
                  : 'Vérifiez le contenu, confirmez cette révision, puis publiez.'}
              </p>
            </div>
            <button
              type='button'
              className='cms-button'
              onClick={refreshChecks}
              disabled={busy}
              aria-label='Revérifier les conditions de publication'
            >
              <RefreshCw size={16} /> Revérifier
            </button>
          </div>
          {blockers.length > 0 && (
            <ol className='cms-publish-blockers'>
              {blockers.map(blocker => (
                <li key={blocker.id}>
                  <div>
                    <strong>{blocker.title}</strong>
                    <p>{blocker.detail}</p>
                    <small>
                      {blocker.action === 'operator'
                        ? 'À réaliser par BoulesConnect'
                        : blocker.action === 'finish'
                          ? 'Action nécessaire avant publication'
                          : 'Action possible ici'}
                    </small>
                  </div>
                  {((blocker.action === 'save' && onSave) ||
                    (blocker.action === 'pages' && onNavigate) ||
                    (blocker.action === 'documents' && onNavigate) ||
                    blocker.action === 'retry') && (
                    <button type='button' className='cms-button' onClick={() => openBlocker(blocker)} disabled={busy}>
                      {blocker.action === 'save' ? 'Enregistrer' : blocker.action === 'retry' ? 'Réessayer' : 'Ouvrir'}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
          {stepCount > 0 && (
            <details className='cms-readiness-steps'>
              <summary>
                Vérifications techniques : {readyCount} / {stepCount} validées
              </summary>
              <ul>
                {state.data?.readiness?.map(check => (
                  <li key={check.id} className={`cms-readiness-${check.status}`}>
                    <span>
                      {check.status === 'ready' ? (
                        <CheckCircle2 size={16} />
                      ) : check.status === 'waiting' ? (
                        '…'
                      ) : (
                        <AlertCircle size={16} />
                      )}
                    </span>
                    <div>
                      <strong>{check.label}</strong>
                      {check.status !== 'ready' && (
                        <small>
                          {check.status === 'waiting' ? 'En attente d’une étape précédente.' : check.detail}
                        </small>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className='cms-hint'>
                Ces contrôles vérifient la configuration déclarée ; le publisher valide encore chaque activation.
              </p>
            </details>
          )}
          {import.meta.env.DEV && domain.data?.hostname === 'demo.boulesconnect.fr' && (
            <div className='cms-local-preview-notice'>
              <strong>Tu testes en local ?</strong> Le site visible sur{' '}
              <a href='http://localhost:18081/' target='_blank' rel='noreferrer'>
                localhost:18081
              </a>{' '}
              est un aperçu statique indépendant. Après enregistrement, utilise <code>pnpm cms:demo:refresh</code> pour
              actualiser cette démo. Cela ne publie pas le vrai domaine ni ne contourne sa sécurité.
            </div>
          )}
          {operatorBlocked && (
            <p className='cms-hint'>
              <strong>Besoin de BoulesConnect :</strong> les validations indiquées « À réaliser par BoulesConnect » ne
              sont pas modifiables dans cet espace organisateur. Il n’y a rien à cocher pour les ignorer.
            </p>
          )}
        </section>
        <label className='cms-consent'>
          <input
            type='checkbox'
            checked={confirmed}
            disabled={!canReview}
            onChange={e => setReviewed(e.target.checked ? review : null)}
          />
          J’ai vérifié cette révision et les fichiers à rendre publics.
        </label>
        {reviewed && !confirmed && (
          <p role='status' className='cms-hint'>
            La version a changé depuis votre vérification. Une nouvelle confirmation est nécessaire.
          </p>
        )}
        <button
          type='button'
          className='cms-button cms-button-primary'
          aria-describedby='cms-publish-explanation'
          disabled={!publishable || !confirmed}
          onClick={() => publish.mutate({ submitted: draft, reviewKey: review })}
        >
          {publish.isPending ? <Loader2 size={17} className='animate-spin' /> : <Rocket size={17} />}
          {publish.isPending ? 'Publication en cours…' : 'Publier cette version'}
        </button>
        {!blockers.length && !confirmed && (
          <p className='cms-hint'>
            Pour activer « Publier cette version », cochez la confirmation ci-dessus après avoir vérifié l’aperçu.
          </p>
        )}
        <details className='cms-advanced'>
          <summary>Préparation avancée</summary>
          <p>Crée une version privée immuable, sans modifier celle en ligne.</p>
          <button
            type='button'
            className='cms-button'
            disabled={!domain.data?.verifiedAt || !canReview || alreadyOnline}
            onClick={() => prepare.mutate(draft.revision)}
          >
            Préparer sans publier
          </button>
        </details>
      </section>
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <h3>Versions conservées</h3>
          <ShieldCheck size={22} />
        </div>
        <p className='cms-hint'>La date indiquée est celle de préparation, pas la date d’activation.</p>
        {(state.data?.history ?? []).map(version => (
          <div className='cms-release-row' key={version.id}>
            <span>
              <b>Révision {version.revision}</b>
              <small className='cms-muted'>
                Préparée le {new Date(version.createdAt).toLocaleString('fr-FR')} ·{' '}
                {
                  { PREPARED: 'Préparée', STAGED: 'Conservée', LIVE: 'En ligne', FAILED: 'Échec', REVOKED: 'Retirée' }[
                    version.status
                  ]
                }
              </small>
            </span>
            {version.status === 'STAGED' && (
              <button
                type='button'
                className='cms-button'
                disabled={busy || libraryBusy || unavailable || loading || !state.data?.canPublish}
                onClick={async () => {
                  if (
                    (await ask({
                      title: `Restaurer la révision ${version.revision} ?`,
                      message:
                        'Cette version remplacera le site public. Votre brouillon actuel restera conservé et ne sera pas remplacé.',
                      choices: [{ value: 'restore', label: 'Restaurer cette version' }],
                    })) === 'restore'
                  )
                    restore.mutate(version.id);
                }}
              >
                Restaurer
              </button>
            )}
          </div>
        ))}
        {!state.isPending && !state.isError && !state.data?.history.length && <p>Aucune version préparée.</p>}
        {live && (
          <button
            type='button'
            className='cms-button cms-danger'
            disabled={busy || libraryBusy}
            onClick={async () => {
              if (
                (await ask({
                  title: 'Retirer le site du web ?',
                  message:
                    'Les pages et les PDF publics ne seront plus diffusés. Vos brouillons et la bibliothèque privée seront conservés.',
                  choices: [{ value: 'revoke', label: 'Retirer le site', danger: true }],
                })) === 'revoke'
              )
                unpublish.mutate();
            }}
          >
            Retirer le site du web
          </button>
        )}
      </section>
    </div>
  );
}
